// i18n
import { TFunction } from "i18next";

// Types
import { Section, Step } from "./types";

export const SECTION_ORDER: Section[] = [
  'learning_preferences',
  'study_style',
  'goals_motivation',
  'learning_challenges',
  'device_access',
  'feedback_motivation',
  'emotional_state',
]

export type Option = {
  value: string;
  label: string;
}

const getLearningPreferencesStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'content_type':
      return [
        { value: 'direct_answers', label: t('onboarding.learning_preferences.content_type.options.direct_answers') },
        { value: 'listening_to_audio', label: t('onboarding.learning_preferences.content_type.options.listening_audio') },
        { value: 'reading_text', label: t('onboarding.learning_preferences.content_type.options.reading') },
        { value: 'real_world_examples', label: t('onboarding.learning_preferences.content_type.options.real_world_examples') },
        { value: 'step_by_step_breakdown', label: t('onboarding.learning_preferences.content_type.options.step_by_step') },
        { value: 'visual_diagrams', label: t('onboarding.learning_preferences.content_type.options.visual_diagrams') },
        { value: 'watching_videos', label: t('onboarding.learning_preferences.content_type.options.watching_video') },
      ]
    case 'retention_method':
      return [
        { value: 'flashcards', label: t('onboarding.learning_preferences.retention_method.options.flashcards') },
        { value: 'practice_quizzes', label: t('onboarding.learning_preferences.retention_method.options.practice_quizzes') },
        { value: 'rewriting_notes', label: t('onboarding.learning_preferences.retention_method.options.rewriting_notes') },
        { value: 'teaching_others', label: t('onboarding.learning_preferences.retention_method.options.teaching_others') },
        { value: 'watching_tutorials', label: t('onboarding.learning_preferences.retention_method.options.watching_tutorials') },
      ]
    case 'feedback_style':
      return [
        { value: 'summarized_feedback', label: t('onboarding.learning_preferences.feedback_style.options.summarized_feedback') },
        { value: 'ongoing_feedback', label: t('onboarding.learning_preferences.feedback_style.options.ongoing_feedback') },
        { value: 'open_ended_feedback', label: t('onboarding.learning_preferences.feedback_style.options.open_ended_feedback') },
        { value: 'no_feedback', label: t('onboarding.learning_preferences.feedback_style.options.no_feedback') },
      ]
    default:
      return [];
  }
};

const getStudyStyleStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'style':
      return [
        { value: 'short_frequent_sessions', label: t('onboarding.study_style.style.options.short_frequent_sessions') },
        { value: 'long_focused_sessions', label: t('onboarding.study_style.style.options.long_focused_sessions') },
        { value: 'mix_of_both', label: t('onboarding.study_style.style.options.mix_of_both') },
        { value: 'not_sure', label: t('onboarding.study_style.style.options.not_sure') },
      ]
    case 'duration':
      return [
        { value: 'under_25_minutes', label: t('onboarding.study_style.duration.options.under_25_minutes') },
        { value: '25_to_45_minutes', label: t('onboarding.study_style.duration.options.25_to_45_minutes') },
        { value: '45_to_90_minutes', label: t('onboarding.study_style.duration.options.45_to_90_minutes') },
        { value: '90_plus_minutes', label: t('onboarding.study_style.duration.options.90_plus_minutes') },
      ]
    case 'period':
      return [
        { value: 'morning', label: t('onboarding.study_style.period.options.morning') },
        { value: 'afternoon', label: t('onboarding.study_style.period.options.afternoon') },
        { value: 'evening', label: t('onboarding.study_style.period.options.evening') },
        { value: 'late_night', label: t('onboarding.study_style.period.options.late_night') },
      ]
    default:
      return [];
  }
}

const getGoalsMotivationStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'motivation':
      return [
        { value: 'passing_an_exam', label: t('onboarding.goals_motivation.motivation.options.passing_an_exam') },
        { value: 'understanding_topics', label: t('onboarding.goals_motivation.motivation.options.understanding_topics') },
        { value: 'getting_into_college', label: t('onboarding.goals_motivation.motivation.options.getting_into_college') },
        { value: 'getting_good_grades', label: t('onboarding.goals_motivation.motivation.options.getting_good_grades') },
      ]
    case 'subjects':
      return [
        { value: 'math', label: t('onboarding.goals_motivation.subjects.options.math') },
        { value: 'science', label: t('onboarding.goals_motivation.subjects.options.science') },
        { value: 'history', label: t('onboarding.goals_motivation.subjects.options.history') },
        { value: 'literature', label: t('onboarding.goals_motivation.subjects.options.literature') },
      ]
    case 'challenges':
      return [
        { value: 'staying_focused', label: t('onboarding.goals_motivation.challenges.options.staying_focused') },
        { value: 'remembering_information', label: t('onboarding.goals_motivation.challenges.options.remembering_information') },
        { value: 'finishing_tasks', label: t('onboarding.goals_motivation.challenges.options.finishing_tasks') },
        { value: 'understanding_instructions', label: t('onboarding.goals_motivation.challenges.options.understanding_instructions') },
        { value: 'procrastination', label: t('onboarding.goals_motivation.challenges.options.procrastination') },
      ]
    default:
      return [];
  }
}

const getLearningChallengesStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'conditions':
      return [
        { value: 'ADHD', label: t('onboarding.learning_challenges.conditions.options.ADHD') },
        { value: 'dyslexia', label: t('onboarding.learning_challenges.conditions.options.dyslexia') },
        { value: 'autism_spectrum', label: t('onboarding.learning_challenges.conditions.options.autism_spectrum') },
        { value: 'auditory_processing_issues', label: t('onboarding.learning_challenges.conditions.options.auditory_processing_issues') },
        { value: 'none_prefer_not_to_say', label: t('onboarding.learning_challenges.conditions.options.none_prefer_not_to_say') },
      ]
    case 'challenges':
      return [
        { value: 'constant', label: t('onboarding.learning_challenges.challenges.options.constant') },
        { value: 'depends_subject', label: t('onboarding.learning_challenges.challenges.options.depends_subject') },
        { value: 'rare', label: t('onboarding.learning_challenges.challenges.options.rare') },
        { value: 'figuring_out_what_to_study', label: t('onboarding.learning_challenges.challenges.options.figuring_out_what_to_study') },
      ]
    case 'change_layout':
      return [
        { value: 'yes', label: t('onboarding.learning_challenges.change_layout.options.yes') },
        { value: 'not_now', label: t('onboarding.learning_challenges.change_layout.options.not_now') },
      ]
    default:
      return [];
  }
}

const getDeviceAccessStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'device':
      return [
        { value: 'mobile_phone', label: t('onboarding.device_access.device.options.mobile_phone') },
        { value: 'tablet', label: t('onboarding.device_access.device.options.tablet') },
        { value: 'computer', label: t('onboarding.device_access.device.options.computer') },
      ]
    case 'accessibility':
      return [
        { value: 'high_contrast', label: t('onboarding.device_access.accessibility.options.high_contrast') },
        { value: 'screen_reader', label: t('onboarding.device_access.accessibility.options.screen_reader') },
        { value: 'closed_captions', label: t('onboarding.device_access.accessibility.options.closed_captions') },
        { value: 'keyboard_navigation', label: t('onboarding.device_access.accessibility.options.keyboard_navigation') },
        { value: 'none', label: t('onboarding.device_access.accessibility.options.none') },
      ]
    default:
      return [];
  }
}

const getFeedbackMotivationStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'style':
      return [
        { value: 'summarized_feedback', label: t('onboarding.feedback_motivation.style.options.summarized_feedback') },
        { value: 'ongoing_feedback', label: t('onboarding.feedback_motivation.style.options.ongoing_feedback') },
        { value: 'open_ended_feedback', label: t('onboarding.feedback_motivation.style.options.open_ended_feedback') },
        { value: 'no_feedback', label: t('onboarding.feedback_motivation.style.options.no_feedback') },
      ]
    case 'frequency':
      return [
        { value: 'daily', label: t('onboarding.feedback_motivation.frequency.options.daily') },
        { value: '2_3_times_a_week', label: t('onboarding.feedback_motivation.frequency.options.2_3_times_a_week') },
        { value: 'weekly', label: t('onboarding.feedback_motivation.frequency.options.weekly') },
        { value: 'when_behind', label: t('onboarding.feedback_motivation.frequency.options.when_behind') },
      ]
    case 'tone':
      return [
        { value: 'gentle_encouragement', label: t('onboarding.feedback_motivation.tone.options.gentle_encouragement') },
        { value: 'progress_tracking_goals', label: t('onboarding.feedback_motivation.tone.options.progress_tracking_goals') },
        { value: 'competitive_gamified', label: t('onboarding.feedback_motivation.tone.options.competitive_gamified') },
        { value: 'show_me_no_nudges', label: t('onboarding.feedback_motivation.tone.options.show_me_no_nudges') },
      ]
    default:
      return [];
  }
}

const getEmotionalStateStepOptions = (stepId: string, t: TFunction): Option[] => {
  switch (stepId) {
    case 'level':
      return [
        { value: 'relaxed', label: t('onboarding.emotional_state.level.options.relaxed') },
        { value: 'fine', label: t('onboarding.emotional_state.level.options.fine') },
        { value: 'a_bit_stressed', label: t('onboarding.emotional_state.level.options.a_bit_stressed') },
        { value: 'stressed', label: t('onboarding.emotional_state.level.options.stressed') },
        { value: 'overwhelmed', label: t('onboarding.emotional_state.level.options.overwhelmed') },
      ]
    case 'relief':
      return [
        { value: 'yes', label: t('onboarding.emotional_state.relief.options.yes') },
        { value: 'maybe_later', label: t('onboarding.emotional_state.relief.options.maybe_later') },
        { value: 'no', label: t('onboarding.emotional_state.relief.options.no') },
      ]
    default:
      return [];
  }
}

export const getOptions = (section: Section, stepId: string, t: TFunction): Option[] => {
  switch (section) {
    case 'learning_preferences':
      return getLearningPreferencesStepOptions(stepId, t);
    case 'study_style':
      return getStudyStyleStepOptions(stepId, t);
    case 'goals_motivation':
      return getGoalsMotivationStepOptions(stepId, t);
    case 'learning_challenges':
      return getLearningChallengesStepOptions(stepId, t);
    case 'device_access':
      return getDeviceAccessStepOptions(stepId, t);
    case 'feedback_motivation':
      return getFeedbackMotivationStepOptions(stepId, t);
    case 'emotional_state':
      return getEmotionalStateStepOptions(stepId, t);
    default:
      return [];
  }
}

export const getSectionSteps: (section: Section, t: TFunction) => Step[] = (section, t) => {
  switch (section) {
    case 'learning_preferences':
      return [
        {
          id: 'content_type',
          title: t('onboarding.learning_preferences.content_type.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
        {
          id: 'retention_method',
          title: t('onboarding.learning_preferences.retention_method.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
        {
          id: 'feedback_style',
          title: t('onboarding.learning_preferences.feedback_style.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
      ];
    case 'study_style':
      return [
        {
          id: 'style',
          title: t('onboarding.study_style.style.title'),
        },
        {
          id: 'duration',
          title: t('onboarding.study_style.duration.title'),
        },
        {
          id: 'period',
          title: t('onboarding.study_style.period.title'),
        },
      ];
    case 'goals_motivation':
      return [
        {
          id: 'motivation',
          title: t('onboarding.goals_motivation.motivation.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
        {
          id: 'subjects',
          title: t('onboarding.goals_motivation.subjects.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
          allowCustom: true,
        },
        {
          id: 'challenges',
          title: t('onboarding.goals_motivation.challenges.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
      ];
    case 'learning_challenges':
      return [
        {
          id: 'conditions',
          title: t('onboarding.learning_challenges.conditions.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
        {
          id: 'challenges',
          title: t('onboarding.learning_challenges.challenges.title'),
          allowCustom: true,
        },
        {
          id: 'change_layout',
          title: t('onboarding.learning_challenges.change_layout.title'),
        },
      ];
    case 'device_access':
      return [
        {
          id: 'device',
          title: t('onboarding.device_access.device.title'),
        },
        {
          id: 'accessibility',
          title: t('onboarding.device_access.accessibility.title'),
          helpText: t('onboarding.common.select_all_that_apply'),
          multiple: true,
        },
      ];
    case 'feedback_motivation':
      return [
        {
          id: 'style',
          title: t('onboarding.feedback_motivation.style.title'),
        },
        {
          id: 'frequency',
          title: t('onboarding.feedback_motivation.frequency.title'),
        },
        {
          id: 'tone',
          title: t('onboarding.feedback_motivation.tone.title'),
        },
      ];
    case 'emotional_state':
      return [
        {
          id: 'level',
          title: t('onboarding.emotional_state.level.title'),
        },
        {
          id: 'relief',
          title: t('onboarding.emotional_state.relief.title'),
        },
      ];
    default:
      return [];
  }
}
